// @ts-nocheck
import { useCallback, useEffect, useState, useRef } from 'react';
import { ErrorToast } from '../utils/alerts';
import { useTranslation } from 'react-i18next';
import {
  deleteTripAmenity,
  getTripAmenity,
  updateTripAmenity,
  updateTripAmenitySequence,
  createTripAmenity
} from '../services/trip/trip';
import { useSelector, useDispatch } from 'react-redux';
import {
  ADD_AMENITY_FROM_LIBRARY,
  REORGANIZE_AMENITIES
} from '../constants/dragAndDrop';
import { cleanDraggableAction } from '../redux/slices/library/library';

const useTripAmenities = (tripid = '') => {
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddAmenityFormVisible, setIsAddAmenityFormVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteAmenityId, setDeleteAmenityId] = useState(null);
  const isProcessingAction = useRef(false);

  const dispatch = useDispatch();
  const draggableAction = useSelector(
    (state) => state.library?.itinerary?.draggableAction ?? {}
  );

  const libraryItems = useSelector((state) => state.library?.items ?? []);

  const { t } = useTranslation(['errors']);

  const handleDeleteAmenityConfirmation = async () => {
    try {
      await deleteTripAmenity(deleteAmenityId);
      setAmenitiesList((prev) =>
        prev.filter((amenity) => amenity.tripamenityid !== deleteAmenityId)
      );
    } catch (error) {
      ErrorToast(
        t('errors:amenityCouldNotBeDeleted', { reason: error.message })
      );
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteAmenityId(null);
    }
  };

  const handleUpdateAmenity = async (amenity) => {
    try {
      setIsSubmitting(true);

      await updateTripAmenity(amenity.tripamenityid, amenity);

      // Update the amenities list with the updated amenity
      setAmenitiesList((prevList) =>
        prevList.map((item) =>
          item.tripamenityid === amenity.tripamenityid ? amenity : item
        )
      );
    } catch (error) {
      ErrorToast(
        t('errors:amenityCouldNotBeUpdated', { reason: error.message })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAmenities = useCallback(async () => {
    try {
      const amenities = await getTripAmenity(tripid);
      const list = (amenities?.data ?? []).sort((a, b) => a?.seq - b?.seq);
      setAmenitiesList(list);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    }
  }, [tripid]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const handleUpdateSequence = (sourceAmenity, destinationAmenity) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const sourceIndex = amenitiesList.findIndex(
      (amenity) => amenity.tripamenityid === sourceAmenity.tripamenityid
    );

    const destIndex = amenitiesList.findIndex(
      (amenity) => amenity.tripamenityid === destinationAmenity.tripamenityid
    );

    if (sourceIndex === -1 || destIndex === -1) {
      setIsSubmitting(false);
      return;
    }

    // Create a copy of the current list to manipulate
    const newList = [...amenitiesList];

    // Remove the item from its original position
    const [movedItem] = newList.splice(sourceIndex, 1);

    // Insert it at the new position
    newList.splice(destIndex, 0, movedItem);

    // Update sequences based on new positions
    const updatedList = newList.map((item, index) => ({
      ...item,
      seq: index + 1
    }));

    // Optimistically update the UI
    setAmenitiesList(updatedList);

    // Prepare payload for the API
    const sequenceData = {
      sequence: destIndex + 1
    };

    // Send update to server with correct parameters
    updateTripAmenitySequence(tripid, sourceAmenity.tripamenityid, sequenceData)
      .then(() => {
        // Nothing extra needed since we already updated optimistically
        // Clear the draggable action to prevent repeats
        dispatch(cleanDraggableAction());
      })
      .catch((error) => {
        console.error('Error updating sequence:', error);
        // Revert optimistic update on error
        fetchAmenities();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDeleteAmenity = async (amenityId) => {
    setIsDeleteModalOpen(true);
    setDeleteAmenityId(amenityId);
  };

  const handleAddAmenityFromLibrary = useCallback(
    async ({ amenity, seq }) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const newAmenity = {
        tripid,
        amenityid: amenity.amenityid,
        name: amenity.name,
        description: amenity.description,
        isincluded: amenity.isincluded,
        seq: seq + 1
      };

      // Optimistically update the UI
      const updatedList = [...amenitiesList];
      updatedList.splice(seq, 0, newAmenity);

      // Update sequences for all items after the insertion point
      const finalList = updatedList.map((item, index) => ({
        ...item,
        seq: index + 1
      }));

      setAmenitiesList(finalList);

      // Send to server
      createTripAmenity(newAmenity)
        .then((response) => {
          // Update the list with the server response to get the tripamenityid
          const serverAmenity = response.data;
          const finalList = updatedList.map((item) => {
            if (item.amenityid === amenity.amenityid && !item.tripamenityid) {
              return {
                ...item,
                tripamenityid: serverAmenity.tripamenityid
              };
            }
            return item;
          });
          setAmenitiesList(finalList);
          dispatch(cleanDraggableAction());
        })
        .catch((error) => {
          console.error('Error adding amenity:', error);
          // Revert optimistic update on error
          setAmenitiesList(amenitiesList);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [amenitiesList, dispatch, isSubmitting, tripid]
  );

  useEffect(() => {
    const processDraggableAction = async () => {
      // Skip if we're already processing an action
      if (isProcessingAction.current || isSubmitting) {
        return;
      }

      const actionType = draggableAction?.type;

      // No action to process
      if (!actionType) {
        return;
      }

      // Check if the action is for us to handle
      if (actionType === ADD_AMENITY_FROM_LIBRARY) {
        const { draggableId } = draggableAction;
        if (!draggableId) return;

        isProcessingAction.current = true;

        try {
          // Extract the amenity ID from the draggable ID (format: libraryamenities.1234)
          const amenityIdMatch = draggableId.match(/libraryamenities\.(\d+)/);
          if (!amenityIdMatch || !amenityIdMatch[1]) {
            console.error('Invalid draggableId format:', draggableId);
            return;
          }

          const amenityId = parseInt(amenityIdMatch[1], 10);
          const amenity = libraryItems.find(
            (item) => item.amenityid === amenityId
          );

          if (amenity) {
            await handleAddAmenityFromLibrary({
              amenity,
              seq: draggableAction.destinationIndex
            });
          } else {
            console.error('Amenity not found in library items:', amenityId);
          }
        } catch (error) {
          console.error('Error processing draggable action:', error);
        } finally {
          isProcessingAction.current = false;
          dispatch(cleanDraggableAction());
        }
      } else if (actionType === REORGANIZE_AMENITIES) {
        // Sequence updates are handled by the AmenitiesTable component
        isProcessingAction.current = false;
      }
    };

    processDraggableAction();
  }, [
    draggableAction,
    isSubmitting,
    libraryItems,
    dispatch,
    handleAddAmenityFromLibrary
  ]);

  return {
    amenitiesList,
    isSubmitting,
    isAddAmenityFormVisible,
    isDeleteModalOpen,
    handleDeleteAmenityConfirmation,
    handleUpdateAmenity,
    handleDeleteAmenity,
    handleUpdateSequence,
    setIsAddAmenityFormVisible,
    setAmenitiesList,
    setIsDeleteModalOpen,
    setDeleteAmenityId,
    handleAddAmenityFromLibrary
  };
};

export default useTripAmenities;
